import React from "react";
import type { UserStats } from "@shukan/shared";

interface Props {
  stats: UserStats;
}

function getLevelInfo(level: number) {
  if (level < 5) {
    return {
      color: "#8a8a8a",
      title: "Greenhorn",
      avatar: `
  ( ^_^)
  /|_|\\
  /   \\
      `,
      tier: "Starter",
    };
  }
  if (level < 8) {
    return {
      color: "#4ade80",
      title: "Novice Planer",
      avatar: `
  [  -_- ]
  /|▓▓▓|\\
  /   \\
      `,
      tier: "Novice",
    };
  }
  if (level < 10) {
    return {
      color: "#0284c7",
      title: "Aurora Seeker",
      avatar: `
  \\('o')/
  /|▓▓▓|\\
  /   \\
      `,
      tier: "Novice",
    };
  }
  if (level < 12) {
    return {
      color: "#10b981",
      title: "Mint Alchemist",
      avatar: `
  (•‿•)
  /|███|\\
  /   \\
      `,
      tier: "Apprentice",
    };
  }
  if (level < 15) {
    return {
      color: "#ef4444",
      title: "Volcanic Druid",
      avatar: `
 (🔥_🔥)
 /|███|\\
 /   \\
      `,
      tier: "Apprentice",
    };
  }
  if (level < 18) {
    return {
      color: "#8b5cf6",
      title: "Cyber Warrior",
      avatar: `
  (★_★)
/|⚡▓⚡|\\
 /     \\
      `,
      tier: "Apprentice",
    };
  }
  if (level < 20) {
    return {
      color: "#059669",
      title: "Forest Ranger",
      avatar: `
  (☘_☘)
  /|▓▓▓|\\
  /   \\
      `,
      tier: "Advanced",
    };
  }
  if (level < 22) {
    return {
      color: "#3b82f6",
      title: "Ocean Vanguard",
      avatar: `
  (⚙_⚙)
  /|☸▓☸|\\
  /   \\
      `,
      tier: "Advanced",
    };
  }
  if (level < 25) {
    return {
      color: "#ff007f",
      title: "Retro Runner",
      avatar: `
 (⚡_⚡)
 /|█▓█|\\
 /   \\
      `,
      tier: "Advanced",
    };
  }
  if (level < 30) {
    return {
      color: "#f97316",
      title: "Solar Knight",
      avatar: `
 (☀️_☀️)
/|⚔️█⚔️|\\
 /     \\
      `,
      tier: "Expert",
    };
  }
  if (level < 35) {
    return {
      color: "#f43f5e",
      title: "Cosmic Overlord",
      avatar: `
  (☯_☯)
/|⚛️█⚛️|\\
 /     \\
      `,
      tier: "Expert",
    };
  }
  if (level < 40) {
    return {
      color: "#6366f1",
      title: "Amethyst Mage",
      avatar: `
 (✨_✨)
/|🔮█🔮|\\
 /     \\
      `,
      tier: "Master",
    };
  }
  if (level < 50) {
    return {
      color: "#be123c",
      title: "Dimension Shifter",
      avatar: `
 (👁️_👁️)
/|🌀█🌀|\\
 /     \\
      `,
      tier: "Master",
    };
  }
  return {
    color: "#6b7280",
    title: "Zen Master AGI",
    avatar: `
 (🌟_🌟)
/|☯️█☯️|\\
 /     \\
    `,
    tier: "Ascended",
  };
}

const milestones = [
  { level: 5, reward: "Sunset Glow Theme & 'Sunset Seeker' Title" },
  { level: 8, reward: "Aurora Borealis Theme & 'Aurora Seeker' Title" },
  { level: 10, reward: "Neon Mint Theme & 'Mint Alchemist' Title" },
  { level: 12, reward: "Volcanic Ash Theme & 'Volcanic Druid' Title" },
  { level: 15, reward: "Cyber Purple Theme & 'Cyber Warrior' Title" },
  { level: 18, reward: "Forest Dew Theme & 'Forest Ranger' Title" },
  { level: 20, reward: "Ocean Breeze Theme & 'Ocean Vanguard' Title" },
  { level: 22, reward: "Retro Vapor Theme & 'Retro Runner' Title" },
  { level: 25, reward: "Solar Flare Theme & 'Solar Knight' Title" },
  { level: 30, reward: "Cosmic Fire Theme & 'Cosmic Overlord' Title" },
  { level: 35, reward: "Royal Amethyst Theme & 'Amethyst Mage' Title" },
  { level: 40, reward: "Midnight Cyber Theme & 'Dimension Shifter' Title" },
  { level: 50, reward: "Zen Master Theme & 'Zen Master AGI' Title" },
];

export function UserProfileCard({ stats }: Props) {
  const { totalXp, level, daysTracked } = stats;
  const levelInfo = getLevelInfo(level);
  
  const currentThreshold = 50 * level * level;
  const nextThreshold = 50 * (level + 1) * (level + 1);
  const range = nextThreshold - currentThreshold;
  const progress = Math.max(0, totalXp - currentThreshold);
  const percent = Math.min(100, Math.floor((progress / range) * 100));

  // Build filled vs empty bar
  const barsFilled = Math.min(12, Math.floor(percent / 8));
  const filledStr = "█".repeat(barsFilled);
  const emptyStr = "░".repeat(12 - barsFilled);

  return (
    <div
      style={{
        border: `1px solid ${levelInfo.color}`,
        backgroundColor: "var(--bg-elevated)",
        boxShadow: `0 0 15px ${levelInfo.color}18`,
        fontFamily: "JetBrains Mono, monospace",
        padding: "1rem",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* Header section */}
      <div style={{ display: "flex", justifyContent: "space-between", borderBottom: `1px dashed ${levelInfo.color}40`, paddingBottom: "0.75rem", flexWrap: "wrap", gap: "0.5rem" }}>
        <div>
          <div style={{ fontSize: "1.2rem", fontWeight: "bold", color: levelInfo.color }}>
            {(stats.username || "SHUKAN_USER").toUpperCase()}
          </div>
          <div style={{ fontSize: "0.75rem", color: "var(--fg-dim)", marginTop: "0.1rem" }}>
            {(stats.customTitle || levelInfo.title).toUpperCase()} (TIER: {levelInfo.tier.toUpperCase()})
          </div>
          {stats.customQuote && (
            <div style={{ fontSize: "0.75rem", fontStyle: "italic", color: "var(--fg-dim)", marginTop: "0.25rem" }}>
              "{stats.customQuote}"
            </div>
          )}
        </div>
        <div style={{ textAlign: "right" }}>
          <span
            style={{
              backgroundColor: levelInfo.color,
              color: "#000",
              padding: "0.1rem 0.4rem",
              fontWeight: "bold",
              fontSize: "0.75rem",
              borderRadius: "2px",
            }}
          >
            LV.{level}
          </span>
        </div>
      </div>

      {/* Main card grid */}
      <div style={{ display: "grid", gridTemplateColumns: "100px 1fr", gap: "1rem", alignItems: "center" }}>
        {/* Avatar */}
        <pre
          style={{
            margin: 0,
            fontSize: "0.75rem",
            color: levelInfo.color,
            border: "1px solid var(--border)",
            backgroundColor: "#000",
            padding: "0.5rem",
            width: "100px",
            height: "90px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: "1.1",
            boxSizing: "border-box",
          }}
        >
          {stats.avatarAscii ? stats.avatarAscii.trim() : levelInfo.avatar.trim()}
        </pre>

        {/* Stats and XP bar */}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          <div style={{ display: "flex", gap: "1.5rem", fontSize: "0.75rem", flexWrap: "wrap" }}>
            <div>
              <span style={{ color: "var(--fg-dim)" }}>TOTAL XP: </span>
              <strong style={{ color: levelInfo.color }}>{totalXp} XP</strong>
            </div>
            <div>
              <span style={{ color: "var(--fg-dim)" }}>DAYS TRACKED: </span>
              <strong style={{ color: "var(--fg)" }}>{daysTracked} DAYS</strong>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.7rem", color: "var(--fg-dim)" }}>
              <span>XP PROGRESS TO NEXT LV</span>
              <span>{percent}%</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.8rem" }}>
              <span style={{ color: "var(--fg-dim)" }}>[</span>
              <span style={{ color: levelInfo.color }}>{filledStr}</span>
              <span style={{ color: "var(--border)" }}>{emptyStr}</span>
              <span style={{ color: "var(--fg-dim)" }}>]</span>
              <span style={{ fontSize: "0.7rem", color: "var(--fg-dim)" }}>
                {totalXp}/{nextThreshold}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Achievement Badges */}
      <div style={{ borderTop: `1px dashed var(--border)`, paddingTop: "0.75rem" }}>
        <div style={{ fontSize: "0.75rem", color: "var(--fg-dim)", marginBottom: "0.5rem" }}>
          &gt; ACHIEVEMENT BADGES
        </div>
        <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
          {[
            { name: "Initiate", desc: "Tracked first habit", icon: "🛡️", unlocked: daysTracked >= 1 },
            { name: "Veteran", desc: "Tracked for 7+ days", icon: "⚔️", unlocked: daysTracked >= 7 },
            { name: "Legendary", desc: "Tracked for 30+ days", icon: "👑", unlocked: daysTracked >= 30 },
            { name: "Apprentice", desc: "Reached Level 5", icon: "🔮", unlocked: level >= 5 },
            { name: "Warrior", desc: "Reached Level 10", icon: "⚡", unlocked: level >= 10 },
            { name: "Master", desc: "Reached Level 25", icon: "☯️", unlocked: level >= 25 },
            { name: "XP Hoarder", desc: "1,000+ total XP", icon: "💰", unlocked: totalXp >= 1000 },
          ].map((b) => (
            <div
              key={b.name}
              title={`${b.name}: ${b.desc} (${b.unlocked ? "Unlocked" : "Locked"})`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.25rem",
                fontSize: "0.65rem",
                padding: "0.15rem 0.35rem",
                border: `1px solid ${b.unlocked ? levelInfo.color : "var(--border)"}`,
                backgroundColor: b.unlocked ? `${levelInfo.color}15` : "transparent",
                opacity: b.unlocked ? 1 : 0.35,
                borderRadius: "2px",
                color: b.unlocked ? "var(--fg)" : "var(--fg-dim)",
              }}
            >
              <span>{b.icon}</span>
              <span>{b.name}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Rewards Milestones timeline */}
      <div style={{ borderTop: `1px dashed var(--border)`, paddingTop: "0.75rem" }}>
        <div style={{ fontSize: "0.75rem", color: "var(--fg-dim)", marginBottom: "0.5rem" }}>
          &gt; LEVEL REWARDS & UNLOCKS
        </div>
        <div
          style={{
            maxHeight: "140px",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: "0.3rem",
            fontSize: "0.7rem",
            paddingRight: "0.25rem",
          }}
        >
          {milestones.map((m) => {
            const isUnlocked = level >= m.level;
            return (
              <div
                key={m.level}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  color: isUnlocked ? "var(--fg)" : "var(--fg-dim)",
                  opacity: isUnlocked ? 1.0 : 0.5,
                  padding: "0.15rem 0.3rem",
                  borderLeft: `2px solid ${isUnlocked ? levelInfo.color : "transparent"}`,
                  backgroundColor: isUnlocked ? "rgba(255,255,255,0.02)" : "transparent",
                }}
              >
                <span style={{ fontWeight: isUnlocked ? "bold" : "normal" }}>
                  {isUnlocked ? "✓" : "🔒"} LV.{m.level} · {m.reward}
                </span>
                <span>{isUnlocked ? "unlocked" : "locked"}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
